import { useUserManagementServiceV1 } from "@rw1/api-services";
import { UserManagement_V1 } from "@rw1/auto-generated-clients";
import { useMemo } from "react";
import { useAllUserGroupsForACompany } from "../../../hooks/useAllUserGroupsForACompany";
import { useFetchAll } from "../../../hooks/useFetchAll";

export const GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY = "groupsForReleaseChannel";

export interface TExperienceGroupRow {
  userGroupId: string;
  userGroupName: string;
  userCount: number;
  experienceCount: number;
  expiresAt: Date | null;
}

interface UseExperienceGroupsParams {
  releaseChannelId?: string;
  companyId?: string;
  enabled?: boolean;
}

export const useExperienceGroups = ({
  releaseChannelId,
  companyId,
  enabled = true,
}: UseExperienceGroupsParams): {
  loading: boolean;
  rows: TExperienceGroupRow[];
} => {
  const userManagement = useUserManagementServiceV1();

  // Fetch group access data (userGroupId and expiresAt)
  const { data: groupAccessData, isLoading: loadingAccess } =
    useFetchAll<UserManagement_V1.UserGroupAccessData>(
      [GROUPS_FOR_RELEASE_CHANNEL_QUERY_KEY, releaseChannelId],
      (page, size) =>
        userManagement.getUserGroupsForReleaseChannel(
          releaseChannelId!,
          page,
          size,
        ),
      { enabled: !!releaseChannelId && enabled },
    );

  const { groups: allCompanyGroups, isLoading: loadingGroups } =
    useAllUserGroupsForACompany(companyId, true);

  // Map groups to rows
  const rows = useMemo<TExperienceGroupRow[]>(() => {
    if (!allCompanyGroups.length || !groupAccessData) return [];

    // Create map of group IDs with expiration dates
    const accessMap = new Map<string, Date | null>();

    groupAccessData.forEach((entry) => {
      const groupId = entry.userGroupId;
      const expiresAt = entry.expiresAt;

      if (groupId) {
        accessMap.set(groupId, expiresAt ?? null);
      }
    });

    // Filter company groups to only those in the access data
    return allCompanyGroups
      .filter((group) => accessMap.has(group.userGroupId))
      .map((group) => ({
        userGroupId: group.userGroupId,
        userGroupName: group.userGroupName,
        userCount: group.userCount,
        experienceCount: group.experienceCount,
        expiresAt: accessMap.get(group.userGroupId) ?? null,
      }));
  }, [allCompanyGroups, groupAccessData]);

  return {
    loading: loadingAccess || loadingGroups,
    rows,
  };
};
